import type { FieldKind, RuleOp } from '@leadgab/shared';

/**
 * What the inventory API sends back.
 *
 * The shapes are deliberately thin: the interesting part is that `ItemRow.data`
 * is keyed by whatever fields the workspace declared, so every table column,
 * form control and filter in the UI is built from `FieldRow[]` at runtime
 * rather than written out in advance.
 */

/** What deleting a field would take with it — see the delete route. */
export interface FieldUsage {
  rules: number;
  orphans: number;
}

export interface FieldRow {
  id: number;
  uuid: string;
  key: string;
  label: string;
  kind: FieldKind;
  required: boolean;
  searchable: boolean;
  filterable: boolean;
  isTitle: boolean;
  options: string[];
  position: number;
  /** Percent of the form row. */
  width: number;
  archived: boolean;
  /** Only sent to managers, and only on the fields route. */
  usage?: FieldUsage;
}

export type CellValue = string | number | boolean | string[] | null;

export interface ItemRow {
  id: number;
  uuid: string;
  externalId: string | null;
  title: string;
  data: Record<string, CellValue>;
  images: string[];
  batchId: string | null;
  deleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RuleRow {
  id: number;
  customerName: string;
  customerEmail: string | null;
  customerPhone: string | null;
  criteria: { key: string; label: string; op: RuleOp; value: string }[];
  unsubscribed: boolean;
  notificationCount: number;
  createdAt: string;
}

export interface InventorySettings {
  label: string;
  labelSingular: string;
  identityFieldKeys: string[];
  /** Where the API answers — not necessarily where the app is served from. */
  apiBase: string;
}

/** One line of text for a cell, whatever kind of value it holds. */
export function cellText(value: CellValue | undefined): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

/** The form's text box holds a string; the API wants the field's own shape. */
export function parseCell(kind: FieldKind, text: string): CellValue {
  const trimmed = text.trim();
  if (!trimmed) return null;

  if (kind === 'number') {
    const value = Number(trimmed);
    // Left as text when it is not a number, so the API answers with the message
    // for that field rather than the browser silently sending null.
    return Number.isFinite(value) ? value : trimmed;
  }

  return trimmed;
}
